/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { BuilderPermission } from '@gh/builder-shared-data';
import { GridCellDef } from '@gh/common-internal-shared';
import {
  composeSetters,
  entityValidatorByEntityConfig,
  FieldSetterFn,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  isDirty,
  PlainImmutablePageState,
  setDirty,
  setEntityField,
  UserContext,
} from '@gh/common-shared';
import { AutocompleteItem, EMPTY_ID, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isDateInvalid } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { constant, isDate, isEmpty, mapKeys, mapValues, sortBy, uniqBy } from 'lodash';
import {
  filterPreConstructionWorksheetSchedules,
  generatePreConstructionWorksheetTaskFieldName,
  getPreConstructionWorksheetScheduleId,
  getPreConstructionWorksheetTaskIdFromName,
  PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_BASE_ENTITY_CONFIG,
  PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_DETAILS_INIT,
  PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_TASK_DATE_FIELD_CONFIG,
  PreConstructionWorksheetDetails,
  PreConstructionWorksheetFilter,
  PreConstructionWorksheetSchedule,
  preConstructionWorksheetScheduleArr,
  PreConstructionWorksheetTemplateTask,
  setPreConstructionScheduleComplexField,
} from '../../resources/pre-construction-worksheet.model';

@Injectable()
export class PreConstructionWorksheetScheduleFormState
  extends PlainImmutablePageState<PreConstructionWorksheetDetails> {
  filteredSchedules$ = combineLatest$(
    this.select(({ schedules }) => schedules),
    this.select(({ filter }) => filter)).pipe(
    map$(([schedules, filter]) => filterPreConstructionWorksheetSchedules(schedules, filter)));
  showWithSlabDateOnly$ = this.select(({ filter: { showWithSlabDateOnly } }) => showWithSlabDateOnly);
  hasSalespersonFilter$ = this.select(({ filter: { salespersonIds } }) => !isEmpty(salespersonIds));
  hasJobAdminFilter$ = this.select(({ filter: { jobAdminIds } }) => !isEmpty(jobAdminIds));

  getCellDef: (schedule: PreConstructionWorksheetSchedule, field: string) => GridCellDef;
  validateField: (schedule: PreConstructionWorksheetSchedule, field: string, value: any) => Maybe<ValidationErrors>;
  validate: (schedule: PreConstructionWorksheetSchedule) => Maybe<ValidationErrors>;
  fieldSetter: FieldSetterFn<PreConstructionWorksheetSchedule>;
  history: PreConstructionWorksheetDetails[] = [];
  historyPosition: number = 0;
  historyLength = 13;
  constructor(private userContext: UserContext) {
    super(PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_DETAILS_INIT, FormMode.EditMode);
  }

  get templateTasks(): PreConstructionWorksheetTemplateTask[] {
    return this.value.templateTasks;
  }

  get countOfEditedSchedules(): number {
    return this.value.schedules.filter(isDirty).length;
  }

  get salespersons(): AutocompleteItem[] {
    return sortBy(
      uniqBy(
        this.value.schedules
          .map(({ salespersonId, salespersonName }) => ({ id: salespersonId || EMPTY_ID, name: salespersonName })),
        ({ id }) => id),
      'name');
  }

  get jobAdmins(): AutocompleteItem[] {
    return sortBy(
      uniqBy(
        this.value.schedules
          .map(({ jobAdmin }) => jobAdmin
            ? { id: jobAdmin.userId, name: jobAdmin.userName }
            : { id: EMPTY_ID, name: '' }),
        ({ id }) => id),
      'name');
  }

  get selectedSalespersonIds(): ID[] {
    return this.value.filter.salespersonIds;
  }

  get selectedJobAdminIds(): ID[] {
    return this.value.filter.jobAdminIds;
  }

  get undoDisabled(): boolean {
    return !this.history.length || this.history.length === 1 || this.historyPosition === 0;
  }

  get redoDisabled(): boolean {
    return !this.history.length || this.historyPosition === this.history.length - 1;
  }

  build(details: PreConstructionWorksheetDetails): void {
    this.setMode(this.userContext.hasPermission(BuilderPermission.Schedules.PreConstruction.EDIT)
      ? FormMode.EditMode
      : FormMode.ViewMode);
    this.setValue(details);
    this.resetDirty();

    this.setupEntityConfig();
    this.history = [];
    this.addToHistory(details);
  }

  undo(): void {
    this.historyPosition = this.historyPosition - 1;
    this.setValue(this.history[this.historyPosition]);
  }

  redo(): void {
    this.historyPosition++;
    this.setValue(this.history[this.historyPosition]);
  }

  addToHistory(value: PreConstructionWorksheetDetails): void {
    if (this.historyPosition !== this.history.length - 1) {
      this.history.splice(this.historyPosition + 1);
    }
    if (this.history.length < this.historyLength) {
      this.history.push(value);
    } else {
      this.history.shift();
      this.history.push(value);
    }
    this.historyPosition = this.history.length - 1;
  }

  revert(): void {
    this.setField('schedules', this.value.originalSchedules);
    this.resetDirty();
  }

  setFilter(field: keyof PreConstructionWorksheetFilter, value: any): void {
    this.setField('filter', setEntityField(this.value.filter, field, value));
  }

  setScheduleField(entity: PreConstructionWorksheetSchedule, field: string, value: any): void {
    if (isDate(value) && isDateInvalid(value)) {
      return;
    }
    const isValueEqual = this.isValueEqual(entity, field, value);
    this.setFieldWithDirty(
      'schedules',
      preConstructionWorksheetScheduleArr.update(
        this.value.schedules,
        getPreConstructionWorksheetScheduleId(entity),
        (schedule) => this.fieldSetter(schedule, field, value)));
    if (!isValueEqual) {
      this.addToHistory(this.value);
    }
  }

  isValueEqual(schedule: PreConstructionWorksheetSchedule, name: string, value: any): boolean {
    const templateTaskId = getPreConstructionWorksheetTaskIdFromName(name);
    if (templateTaskId) {
      const current = !!schedule.scheduleTasks[templateTaskId].completionDate
        ? schedule.scheduleTasks[templateTaskId].completionDate?.getTime() : 0;
      const newValue = !!value ? value.getTime() : 0;
      return current === newValue;
    } else {
      if (isDate(schedule[name]) || isDate(value)) {
        const currenValue = schedule[name] && schedule[name].getTime ? schedule[name].getTime() : 0;
        const newScheduleValue = value && value.getTime ? value.getTime() : 0;
        return currenValue === newScheduleValue;
      }
      // tslint:disable-next-line:triple-equals
      return schedule[name] == value;
    }
  }
  restrictEditingToDirtySchedules(): void {
    this.setField(
      'schedules',
      this.value.schedules.map((schedule) =>
        isDirty(schedule) ? schedule : setEntityField(schedule, 'isEditable', false)));
  }

  private setupEntityConfig(): void {
    const entityConfig = {
      ...PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_BASE_ENTITY_CONFIG,
      ...mapValues(
        mapKeys(
          this.value.templateTasks,
          ({ templateTaskId }) => generatePreConstructionWorksheetTaskFieldName(templateTaskId)),
        constant(PRE_CONSTRUCTION_WORKSHEET_SCHEDULE_TASK_DATE_FIELD_CONFIG)),
    };

    this.getCellDef = fieldStateByEntityConfig(entityConfig);
    this.validateField = fieldValidatorByEntityConfig(entityConfig);
    this.validate = entityValidatorByEntityConfig(entityConfig);
    this.fieldSetter = composeSetters(setPreConstructionScheduleComplexField,
                                      setDirty,
                                      fieldValidationSetter(this.validateField));
  }
}
